<template>
  <div>
    <div class="md-layout">
      <div class="md-layout-item md-size-50 md-small-size-100 px-0">
        <md-card class="card-content">
          <md-card-header class="md-card-header-text md-card-header-green">
            <div class="card-text">
              <h4 class="title text-lg">{{ $t("class.primary_teacher") }}</h4>
            </div>
          </md-card-header>
          <md-card-content>
            <md-field v-if="!$can(Class.actions.edit, Class.subject)">
              <label>{{ $t("class.primary_teacher") }}</label>
              <md-input
                v-model.trim="primaryTeacher.displayValue"
                disabled
              ></md-input>
            </md-field>

            <isis-autocomplete
              v-else
              v-model="primaryTeacher.displayValue"
              :value.sync="primaryTeacher.value"
              :options="teacherOpts"
              :refetchOptions="
                queryObj =>
                  primaryTeacher.refetchOptions(primaryTeacher.value, queryObj)
              "
              :search-key="primaryTeacher.searchKey"
              :fields="primaryTeacher.fields"
              :label="primaryTeacher.label"
              fetchOnOpen
            >
            </isis-autocomplete>
          </md-card-content>
        </md-card>
      </div>

      <div class="md-layout-item md-size-50 md-small-size-100 px-0 lg:pl-3">
        <md-card class="card-content">
          <md-card-header class="md-card-header-text md-card-header-green">
            <div class="card-text">
              <h4 class="title text-lg">
                {{ $t("class.secondary_teachers") }}
              </h4>
            </div>
          </md-card-header>

          <md-card-content v-if="!$can(Class.actions.edit, Class.subject)">
            <md-field
              v-for="(teacher, idx) in secondaryTeachers"
              :key="'second' + idx"
            >
              <label> {{ $t("class.secondary_teacher") }} {{ idx + 1 }}</label>
              <md-input v-model.trim="teacher.displayValue" disabled></md-input>
            </md-field>
          </md-card-content>

          <md-card-content v-else>
            <isis-autocomplete
              v-for="(teacher, idx) in secondaryTeachers"
              :key="'second' + idx"
              v-model="teacher.displayValue"
              :value.sync="teacher.value"
              :options="teacherOpts"
              :refetchOptions="
                queryObj => teacher.refetchOptions(teacher.value, queryObj)
              "
              :search-key="teacher.searchKey"
              :fields="teacher.fields"
              :label="$t('class.secondary_teacher') + ` ${idx + 1}`"
              :isSwitchLocale="false"
              fetchOnOpen
            >
            </isis-autocomplete>
          </md-card-content>
        </md-card>
      </div>
    </div>
    <div
      class="form-actions text-center"
      v-if="$can(Class.actions.edit, Class.subject)"
    >
      <md-button class="md-raised" @click="clearForm">{{
        $t("common.cancel")
      }}</md-button>
      <md-button
        class="md-success ml-4"
        @click.native="updateTeacherAssignments"
        >{{ $t("common.update") }}</md-button
      >
    </div>
  </div>
</template>

<script>
import { service_paths, cms_service_paths } from "@/constants/Services";
import { TEACHER_ASSIGNMENTS } from "@/pages/Classes/data";
import { Message } from "@/helpers/utils";
import cloneDeep from "lodash/cloneDeep";
import { class_router_names } from "@/routes/routes";
import { Class } from "@/constants/Permissions";
import {SYSTEM } from "@/constants/System";

export default {
  name: "teacher-list",
  inject: ["getClassInfo"],
  data() {
    return {
      Class,
      defaultTeachers: [],
      teacherOpts: [],
      primaryTeacher: {
        label: "class.primary_teacher",
        value: "",
        displayValue: "",
        fields: {
          text: "label",
          value: "username"
        },
        searchKey: "search_key",
        refetchOptions: (selectedValue, queryObj) => {
          return this.getTeacherOpts(selectedValue, queryObj);
        }
      },
      secondaryTeachers: [...Array(5).keys()].map(() => ({
        value: "",
        displayValue: "",
        fields: {
          text: "label",
          value: "username"
        },
        searchKey: "search_key",
        refetchOptions: (selectedValue, queryObj) => {
          return this.getTeacherOpts(selectedValue, queryObj);
        }
      })),
      classId: this.$route.params.id,
      source: ""
    };
  },
  methods: {
    isSelectedValue(selectedValue, teacher) {
      return selectedValue && selectedValue === teacher.value;
    },
    filteredTeacherOpts(selectedValue, teacherOpts) {
      const allSelectedUsn = !this.isSelectedValue(
        selectedValue,
        this.primaryTeacher
      )
        ? [this.primaryTeacher.value]
        : [];
      this.secondaryTeachers.forEach(
        teacher =>
          !this.isSelectedValue(selectedValue, teacher) &&
          allSelectedUsn.push(teacher.value)
      );
      this.teacherOpts = teacherOpts.filter(
        teacher => !allSelectedUsn.includes(teacher.username)
      );
    },
    getTeacherOpts(selectedValue, queryObj) {
      const params = {
        search_key: queryObj?.search_key || "",
        fields: ["full_name", "staff_code"],
        source: this.source
      };
      return this.$axios
        .get(
          `${service_paths.staffs}/filter`,
          params,
          undefined,
          undefined,
          undefined,
          undefined,
          false
        )
        .then(({ data }) => {
          const teacherOpts = data.data.map(teacher => ({
            label: teacher.staff_code
              ? `${teacher.full_name} - ${teacher.staff_code}`
              : teacher.full_name,
            username: teacher.username
          }));
          this.filteredTeacherOpts(selectedValue, teacherOpts);
        });
    },
    generateUpdatingData() {
      const teachers = [];

      // push primary teacher
      this.primaryTeacher.displayValue &&
        teachers.push({
          username: this.primaryTeacher.value,
          assignment: TEACHER_ASSIGNMENTS.PRIMARY
        });

      // push secondary teacher
      this.secondaryTeachers.forEach((teacher, teacherPosition) => {
        if (!teacher.displayValue) return;

        teachers.push({
          position: teacherPosition,
          username: teacher.value,
          assignment: TEACHER_ASSIGNMENTS.SECONDARY
        });
      });
      return {
        teachers
      };
    },

    updateTeacherAssignments() {
      const assignmentData = this.generateUpdatingData();
      if (!assignmentData.teachers.length) {
        Message.error(this.$t("class.please_assign_at_least_1_teacher"));
        return;
      }

      this.$axios
        .post(
          `${service_paths.classes}/${this.classId}/assign-teachers`,
          assignmentData
        )
        .then(({ data }) => {
          Message.success(
            this.$t("class.update_teacher_assignment_successfully")
          );
          this.getClassInfo("update_teacher_list");
          if (this.source == SYSTEM.LMS) {
            let params = data.map(x => {
              return {
                ...x,
                id_ref: x.id,
                class_id_ref: x.class_id,
                teacher_id_ref: x.user_id
              }
            })
            this.$axios.cms_post(`${cms_service_paths.assign_teachers}`, params, {}, {}, null, false)
          }

        });
    },

    initSelections(teachers) {
      teachers = teachers || this.defaultTeachers;

      teachers.forEach(teacher => {
        let scTeacherPosition = teacher.position;
        const userInfo = teacher?.users.user_no_sql;
        const staffCode = userInfo.staff_code
          ? ` - ${userInfo.staff_code}`
          : "";

        if (teacher.assignment === TEACHER_ASSIGNMENTS.PRIMARY) {
          this.primaryTeacher.displayValue = userInfo.full_name + staffCode;
          this.primaryTeacher.value = userInfo.username;
          return;
        }

        if (teacher.assignment === TEACHER_ASSIGNMENTS.SECONDARY) {
          this.$set(
            this.secondaryTeachers[scTeacherPosition],
            "displayValue",
            userInfo.full_name + staffCode
          );
          this.$set(
            this.secondaryTeachers[scTeacherPosition],
            "value",
            userInfo.username
          );
          return;
        }
      });
      this.getTeacherOpts();
    },

    clearForm() {
      this.initSelections();
      this.$router.push({
        name: class_router_names.listPage
      });
    },

    injectFormData(classInfo) {
      this.defaultTeachers = cloneDeep(classInfo.teachers);
      this.source = classInfo?.lms?.name;
      this.initSelections(classInfo.teachers);
    }
  }
};
</script>

<style lang="scss" scoped></style>
